import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const CursorWrapper = styled(motion.div)`
  position: fixed;
  left: 0;
  top: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  pointer-events: none;
  z-index: 9999;
  mix-blend-mode: difference;
  
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    background-color: var(--color-accent-primary);
    border-radius: 50%;
    transition: all 0.2s ease;
  }
`;

const CursorDot = styled(motion.div)`
  position: fixed;
  left: 0;
  top: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--color-accent-primary);
  pointer-events: none;
  z-index: 9999;
`;

const Cursor = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [cursorVariant, setCursorVariant] = useState('default');
  
  useEffect(() => {
    // Add cursor class to body
    document.body.classList.add('has-custom-cursor');
    
    const mouseMove = (e) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    
    // Add hover effect for links and buttons
    const handleLinkHover = () => setCursorVariant('link');
    const handleLinkLeave = () => setCursorVariant('default');
    
    // Add event listeners
    window.addEventListener('mousemove', mouseMove);
    
    // Add hover events to all interactive elements
    const links = document.querySelectorAll('a, button, input, textarea, select, [role="button"]');
    links.forEach(link => {
      link.addEventListener('mouseenter', handleLinkHover);
      link.addEventListener('mouseleave', handleLinkLeave);
    });
    
    // Cleanup
    return () => {
      window.removeEventListener('mousemove', mouseMove);
      document.body.classList.remove('has-custom-cursor');
      
      links.forEach(link => {
        link.removeEventListener('mouseenter', handleLinkHover);
        link.removeEventListener('mouseleave', handleLinkLeave);
      });
    };
  }, []);
  
  // Cursor variants for different states
  const variants = {
    default: {
      x: mousePosition.x - 16,
      y: mousePosition.y - 16,
      backgroundColor: 'rgba(166, 140, 255, 0.2)',
      border: '1px solid rgba(166, 140, 255, 0.3)',
      backdropFilter: 'blur(4px)',
    },
    link: {
      x: mousePosition.x - 24,
      y: mousePosition.y - 24,
      width: 48,
      height: 48,
      backgroundColor: 'rgba(166, 140, 255, 0.3)',
      border: '1px solid rgba(166, 140, 255, 0.5)',
      backdropFilter: 'blur(8px)',
    }
  };
  
  // Small dot that follows the cursor exactly
  const dotVariants = {
    default: {
      x: mousePosition.x - 4,
      y: mousePosition.y - 4,
      transition: {
        type: 'spring',
        damping: 50,
        stiffness: 500,
        mass: 0.3
      }
    }
  };

  return (
    <>
      <CursorWrapper 
        variants={variants} 
        animate={cursorVariant} 
      />
      <CursorDot 
        variants={dotVariants} 
        animate="default"
      />
    </>
  );
};

export default Cursor;